/**
 * Deterministic avatar utilities — pure functions, no side effects.
 */

/** Simple string hash (djb2) → positive integer */
function hashString(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i)
  }
  return hash >>> 0
}

/**
 * Returns a deterministic pastel HSL color for a given name.
 * Same name always produces the same color.
 */
export function getAvatarColor(name: string): string {
  const hue = hashString(name.toLowerCase().trim()) % 360
  return `hsl(${hue}, 45%, 80%)`
}

/**
 * Extracts up to 2 uppercase initials from a display name or email.
 *
 * - Display name: "George Doe" → "GD"
 * - Email: "george.doe@acme.com" → "GD"
 * - Fallback: first 2 chars uppercased
 */
export function getInitials(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return "?"

  // Display name (contains space, no @)
  if (!trimmed.includes("@") && trimmed.includes(" ")) {
    const parts = trimmed.split(/\s+/).filter(Boolean)
    return parts
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join("")
  }

  // Email — use local part, split on . - _
  if (trimmed.includes("@")) {
    const local = trimmed.split("@")[0]
    const parts = local.split(/[.\-_]+/).filter(Boolean)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return local.slice(0, 2).toUpperCase()
  }

  // Fallback
  return trimmed.slice(0, 2).toUpperCase()
}
